import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { DialogTitleWithBug } from '@/components/ui/dialog-title-with-bug';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { MessageSquare } from 'lucide-react';
import { useTaskComments } from '@/hooks/useTaskComments';
import { useGrowthSquad } from '@/hooks/useGrowthSquad';
import { MentionTextarea } from '@/components/ui/mention-textarea';

interface AddCommentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  taskId: string;
  taskType: 'foundational' | 'growth' | 'recurring';
  onSuccess?: () => void;
  taskBrandId?: string;
}

export const AddCommentDialog: React.FC<AddCommentDialogProps> = ({
  isOpen,
  onClose,
  taskId,
  taskType,
  onSuccess,
  taskBrandId,
}) => {
  const [commentText, setCommentText] = useState('');
  const [mentions, setMentions] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { addComment } = useTaskComments(taskId, taskType, taskBrandId);
  const { squadMembers } = useGrowthSquad();

  const mentionOptions = squadMembers.map(member => ({
    id: member.id,
    name: member.name,
    alias: member.alias,
  }));

  const handleSubmit = async () => {
    if (!commentText.trim()) return;

    console.log('AddCommentDialog: Submitting comment with mentions:', { commentText, mentions });
    try {
      setIsSubmitting(true);
      await addComment(commentText, mentions);
      setCommentText('');
      setMentions([]);
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('AddCommentDialog: Error submitting comment:', error);
      // Error handling is done in the hook
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setCommentText('');
    setMentions([]);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitleWithBug 
            popupName="Add Comment" 
            popupContext="/my-tasks"
            isOpen={isOpen}
          >
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Add New Comment
            </div>
          </DialogTitleWithBug>
          <DialogDescription>
            Add a comment to this task. Use @ to mention team members.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="comment">Comment</Label>
            <MentionTextarea
              value={commentText}
              onChange={setCommentText}
              onMentionsChange={setMentions}
              mentionOptions={mentionOptions}
              placeholder="Add your comment... Use @ to mention team members"
              className="min-h-[120px] resize-none"
            />
          </div>
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={!commentText.trim() || isSubmitting}
          >
            {isSubmitting ? 'Adding...' : 'Add Comment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};